import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

/**
 * Yönetici sunumu — 1920×1080 slaytlar ekrana sığacak kadar ölçeklenir.
 * → / boşluk / tık: sonraki (önce slayttaki adımlar açılır) · ← önceki ·
 * N: konuşmacı notu · Esc: panele dön.
 */
type Slayt = { id: string; html: string };

const CSS = `
.snm { position:fixed; inset:0; background:#141110; z-index:1000; overflow:hidden; font-family:'DM Sans', Arial, sans-serif; }
.snm-sahne { position:absolute; left:50%; top:50%; width:1920px; height:1080px; transform-origin:0 0; }
.snm-sahne section { position:relative; width:1920px; height:1080px; box-sizing:border-box; overflow:hidden; }
.snm-sahne section * { margin:0; box-sizing:border-box; }
.snm-sahne aside { display:none; }
.snm-sahne a { color:inherit; text-decoration:none; }
.snm-sahne table { width:100%; border-collapse:separate; border-spacing:0; overflow:hidden; }
.snm-sahne th, .snm-sahne td { padding:18px 26px; border-bottom:1px solid #E3D9D1; }
.snm-sahne tr:last-child td { border-bottom:0; }
.snm-sahne[data-anim="1"] [data-build-in] { transition:opacity .35s ease, transform .35s ease; }
.snm-sahne [data-gizli="1"] { opacity:0; transform:translateY(24px); pointer-events:none; }
.snm-cubuk { position:absolute; left:0; right:0; bottom:0; display:flex; align-items:center; gap:10px; padding:10px 16px;
  color:#B5A99F; font-size:14px; background:linear-gradient(transparent, rgba(0,0,0,.55)); opacity:.0; transition:opacity .2s; }
.snm:hover .snm-cubuk, .snm-cubuk:focus-within { opacity:1; }
.snm-cubuk button { background:rgba(255,255,255,.1); color:#FAF7F3; border:0; border-radius:8px; padding:8px 14px; font:inherit; cursor:pointer; }
.snm-cubuk button:hover { background:rgba(255,255,255,.2); }
.snm-not { position:absolute; left:50%; bottom:56px; transform:translateX(-50%); width:min(1100px, 92vw); max-height:38vh; overflow:auto;
  background:rgba(20,17,16,.94); color:#FAF7F3; border:1px solid #4A413B; border-radius:12px; padding:16px 20px; font-size:17px; line-height:1.5; }
.snm-bos { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#FAF7F3; font-size:18px; }
/* Dokunmatik: üzerine gelme yok, çubuk hep görünür; düğmeler 44 px; çentik / ana ekran çizgisi payı. */
@media (hover: none), (max-width: 900px) {
  .snm-cubuk { opacity:1; flex-wrap:wrap; padding:10px max(12px, env(safe-area-inset-right, 0px)) calc(10px + env(safe-area-inset-bottom, 0px)) max(12px, env(safe-area-inset-left, 0px)); }
  .snm-cubuk button { min-height:44px; min-width:44px; }
  .snm-not { bottom:calc(72px + env(safe-area-inset-bottom, 0px)); font-size:15px; }
}
`;

function adimSayisi(html: string): number {
  let en = 0;
  for (const m of html.matchAll(/data-build-in="[a-z]+ (\d+)"/g)) en = Math.max(en, Number(m[1]));
  return en;
}

export default function Sunum() {
  const git = useNavigate();
  const [slaytlar, setSlaytlar] = useState<Slayt[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [i, setI] = useState(0);
  const [adim, setAdim] = useState(0);
  const [notAcik, setNotAcik] = useState(false);
  const [olcek, setOlcek] = useState(1);
  const sahne = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api<{ slaytlar: Slayt[] }>('/api/sunum').then((r) => setSlaytlar(r.slaytlar))
      .catch((e) => setHata(e instanceof Error ? e.message : 'Sunum açılamadı'));
    if (!document.getElementById('snm-font')) {
      const l = document.createElement('link');
      l.id = 'snm-font'; l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;600&family=DM+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => {
    const olc = () => setOlcek(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
    olc();
    window.addEventListener('resize', olc);
    return () => window.removeEventListener('resize', olc);
  }, []);

  const s = slaytlar?.[i];
  const toplamAdim = useMemo(() => (s ? adimSayisi(s.html) : 0), [s]);
  const not = useMemo(() => s?.html.match(/<aside>([\s\S]*?)<\/aside>/)?.[1] ?? '', [s]);

  const ileri = useCallback(() => {
    if (!slaytlar) return;
    if (adim < toplamAdim) setAdim(adim + 1);
    else if (i < slaytlar.length - 1) { setI(i + 1); setAdim(0); }
  }, [slaytlar, adim, toplamAdim, i]);
  const geri = useCallback(() => {
    if (!slaytlar) return;
    if (adim > 0) setAdim(adim - 1);
    else if (i > 0) { setI(i - 1); setAdim(adimSayisi(slaytlar[i - 1].html)); }
  }, [slaytlar, adim, i]);

  useEffect(() => {
    const tus = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown' || e.key === 'Enter') { e.preventDefault(); ileri(); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp' || e.key === 'Backspace') { e.preventDefault(); geri(); }
      else if (e.key === 'n' || e.key === 'N') setNotAcik((v) => !v);
      else if (e.key === 'f' || e.key === 'F') document.documentElement.requestFullscreen?.().catch(() => undefined);
      else if (e.key === 'Escape') { if (document.fullscreenElement) return; git('/'); }
    };
    window.addEventListener('keydown', tus);
    return () => window.removeEventListener('keydown', tus);
  }, [ileri, geri, git]);

  /* Adımlar: sırası gelmeyen parçalar gizli kalır. Slayt değişince geçiş
     efekti kapalı başlar; yoksa gizlenecek parçalar bir an görünür. */
  const sonSlayt = useRef<string | null>(null);
  useLayoutEffect(() => {
    const kok = sahne.current;
    if (!kok) return;
    const yeni = sonSlayt.current !== (s?.id ?? null);
    sonSlayt.current = s?.id ?? null;
    if (yeni) kok.dataset.anim = '0';
    kok.querySelectorAll<HTMLElement>('[data-build-in]').forEach((el) => {
      const n = Number(el.dataset.buildIn?.split(' ')[1] ?? 0);
      el.dataset.gizli = n > adim ? '1' : '0';
    });
    if (yeni) requestAnimationFrame(() => requestAnimationFrame(() => { kok.dataset.anim = '1'; }));
  }, [s, adim]);

  return (
    <div className="snm">
      <style>{CSS}</style>
      {hata ? <div className="snm-bos">{hata}</div> : !s ? <div className="snm-bos">Yükleniyor…</div> : (
        <div
          ref={sahne} className="snm-sahne"
          style={{ transform: `scale(${olcek}) translate(-50%, -50%)` }}
          onClick={(e) => { if (!(e.target as HTMLElement).closest('a')) ileri(); }}
          dangerouslySetInnerHTML={{ __html: s.html }}
        />
      )}
      {notAcik && not && <div className="snm-not">{not}</div>}
      {slaytlar && (
        <div className="snm-cubuk">
          <button onClick={() => git('/')}>← Panele dön</button>
          <button onClick={geri}>Önceki</button>
          <button onClick={ileri}>Sonraki</button>
          <span>{i + 1} / {slaytlar.length}</span>
          <span style={{ flex: 1 }} />
          <button onClick={() => setNotAcik((v) => !v)}>{notAcik ? 'Notu gizle' : 'Konuşmacı notu (N)'}</button>
          <button onClick={() => document.documentElement.requestFullscreen?.().catch(() => undefined)}>Tam ekran (F)</button>
        </div>
      )}
    </div>
  );
}
